<template>
  <!--
    WHAT: Editor for a worklist's column_config — the display projection over the
          joined steps. Each row is pure display metadata (field, header, type,
          visibility, order) plus an optional source_column alias and a default
          for LEFT-join misses. No expression/aggregation machinery — grain and
          aggregation live in the step DAs.
    WHERE: [reused verbatim from v1] Wrapped by WorklistColumnsSection.vue (the
           worklist "Columns" detail tab).
    CONTRACT:
      props:
        modelValue (Array, v-model) — column_config: [{field, header, type,
                                      source_column?, default?, default_visible,
                                      order}]
        steps (Array)               — join steps; kept for parent compatibility,
                                      not used here
        outputColumns (Array)       — live join output columns, to populate the
                                      optional "Src col" picker
      emits: update:modelValue
    NOTES: [reused verbatim from v1] shared component — do NOT restyle without
           checking other callers. Gotchas: emit CLEANS rows — source_column is
           only written when it differs from field (case-insensitive), and blank
           defaults are dropped; a `_lastEmitted` guard prevents the incoming
           modelValue watcher from clobbering in-progress edits.
  -->
  <div>
    <div class="flex items-center justify-between mb-2.5">
      <span class="text-xs font-semibold text-gray-600">Columns ({{ cols.length }})</span>
      <button class="font-mono text-[11px] text-blue-600 hover:text-blue-700" @click="addColumn">＋ add</button>
    </div>

    <div v-if="cols.length" class="space-y-2 ms-scroll overflow-y-auto" style="max-height:520px;">
      <div
        v-for="(col, i) in cols"
        :key="i"
        class="border border-gray-100 rounded-lg p-2.5 bg-gray-50"
      >
        <div class="flex items-center gap-1.5 flex-wrap">
          <span class="text-[10px] text-gray-400 w-4 text-right shrink-0">{{ i + 1 }}</span>
          <input v-model="col.field" placeholder="field_name" class="text-xs px-2 py-1 border border-gray-200 rounded w-32 text-gray-800 font-mono focus:outline-none" @input="emit" />
          <input v-model="col.header" placeholder="Display Header" class="text-xs px-2 py-1 border border-gray-200 rounded w-36 text-gray-800 focus:outline-none" @input="emit" />
          <select v-model="col.type" class="text-xs px-2 py-1 border border-gray-200 rounded text-gray-800 focus:outline-none" @change="emit">
            <option value="boolean">Boolean</option>
            <option value="integer">Integer</option>
            <option value="float">Float</option>
            <option value="number">Number</option>
            <option value="date">Date</option>
            <option value="string">String</option>
            <option value="text">Text</option>
          </select>
          <label class="flex items-center gap-1 text-xs text-gray-600 shrink-0">
            <input v-model="col.default_visible" type="checkbox" class="rounded" @change="emit" /> Visible
          </label>
          <input v-model.number="col.order" type="number" min="0" class="text-xs px-2 py-1 border border-gray-200 rounded w-12 text-gray-800 focus:outline-none text-center" placeholder="#" @input="emit" />
          <button class="text-gray-400 hover:text-red-500 text-xs shrink-0" @click="removeColumn(i)">&times;</button>
        </div>

        <!-- Optional: source column alias (when the field name differs from the
             emitted column) and a default for LEFT-join misses. Both optional. -->
        <div class="flex items-center gap-1.5 ml-5 mt-1.5 flex-wrap">
          <label class="text-[10px] text-gray-400">Src col:</label>
          <select
            v-if="outputColumns.length"
            v-model="col.source_column"
            class="text-xs px-2 py-1 border border-gray-200 rounded w-44 text-gray-800 font-mono focus:outline-none"
            @change="emit"
          >
            <option value="">(same as field)</option>
            <option v-for="oc in outputColumns" :key="oc.col || oc" :value="oc.col || oc">{{ oc.col || oc }}</option>
          </select>
          <input v-else v-model="col.source_column" placeholder="(same as field)" class="text-xs px-2 py-1 border border-gray-200 rounded w-44 text-gray-800 font-mono focus:outline-none" @input="emit" />
          <label class="text-[10px] text-gray-400 ml-1">Default:</label>
          <input v-model="col.default" placeholder="(none)" class="text-xs px-2 py-1 border border-gray-200 rounded w-16 text-gray-800 font-mono focus:outline-none" @input="emit" />
        </div>
      </div>
    </div>
    <div v-else class="text-xs text-gray-400 text-center py-4">No columns defined</div>
  </div>
</template>

<script>
// A worklist column is pure display metadata: a real column the joined steps emit.
// No expression/aggregation machinery — grain and aggregation live in the step DAs.
const EMPTY_COLUMN = {
  field: '', header: '', type: 'string',
  source_column: '', default: undefined, default_visible: true, order: 0,
};

export default {
  name: 'WorklistProjectionPanel',
  props: {
    // v-model: column_config array
    modelValue: { type: Array, default: () => [] },
    // join steps (kept for parent compatibility; not used here)
    steps: { type: Array, default: () => [] },
    // live join output columns (to populate the optional Src col picker)
    outputColumns: { type: Array, default: () => [] },
  },
  emits: ['update:modelValue'],
  data() {
    return { cols: (this.modelValue || []).map((c) => ({ ...c })) };
  },
  watch: {
    modelValue(v) {
      if (v !== this._lastEmitted) this.cols = (v || []).map((c) => ({ ...c }));
    },
  },
  methods: {
    addColumn() {
      const nextOrder = this.cols.length ? Math.max(...this.cols.map((c) => c.order || 0)) + 1 : 0;
      this.cols.push({ ...EMPTY_COLUMN, order: nextOrder });
      this.emit();
    },
    removeColumn(i) { this.cols.splice(i, 1); this.emit(); },
    emit() {
      const cleaned = this.cols.map((c) => {
        const col = { field: c.field, header: c.header, type: c.type || 'string' };
        // source_column only when it actually differs from the field name.
        if (c.source_column && c.source_column.toLowerCase() !== (c.field || '').toLowerCase()) {
          col.source_column = c.source_column;
        }
        if (c.default !== undefined && c.default !== null && c.default !== '') col.default = c.default;
        if (c.default_visible !== undefined) col.default_visible = c.default_visible;
        if (c.order !== undefined) col.order = c.order;
        return col;
      });
      this._lastEmitted = cleaned;
      this.$emit('update:modelValue', cleaned);
    },
  },
};
</script>

<style scoped>
.ms-scroll::-webkit-scrollbar { width: 10px; height: 10px; }
.ms-scroll::-webkit-scrollbar-thumb { background: #D1D5DB; border-radius: 10px; border: 3px solid transparent; background-clip: content-box; }
.ms-scroll::-webkit-scrollbar-track { background: transparent; }
</style>
